import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../api";
import { IMG } from "../data/siteData";

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data.posts)).catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title="Dog Training Blog | Tips & Advice | Chinook K9 Calgary"
        description="Dog training tips, behaviour advice and puppy guides from the certified trainers at Chinook K9 — serving Calgary and surrounding Alberta communities."
        path="/blog"
      />
      <section className="bg-obsidian pt-40 pb-16 border-b border-white/10" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4">Blog</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white">
              Training tips & advice
            </h1>
            <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
              Practical guidance from our certified trainers — obedience, behaviour, puppies and life with your dog in Calgary and beyond.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20" data-testid="blog-grid-section">
        <div className="max-w-7xl mx-auto px-6">
          {!posts ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
              {[...Array(3)].map((_, i) => <div key={i} className="h-80 bg-white/5 rounded-2xl" />)}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16" data-testid="blog-empty">
              <p className="text-white/60 text-lg">Articles are on the way — check back soon.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-6 text-brand font-heading font-bold hover:underline">
                Have a training question? Contact us <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {posts.map((p, i) => (
                <Reveal key={p.post_id} delay={(i % 3) * 0.08}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                    className="group block bg-[#121212] border border-white/10 rounded-2xl overflow-hidden hover:border-brand/50 transition-colors h-full">
                    <div className="aspect-[16/10] overflow-hidden bg-black">
                      <img src={p.cover_image || IMG.boardTrain} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <p className="text-xs text-white/40 mb-2">
                        {new Date(p.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                      <h2 className="font-heading text-lg font-bold text-white leading-snug group-hover:text-brand transition-colors">{p.title}</h2>
                      <p className="text-sm text-white/60 mt-2 line-clamp-2 leading-relaxed">{p.excerpt}</p>
                      <span className="inline-flex items-center gap-2 mt-4 text-brand font-heading font-bold text-sm group-hover:gap-3 transition-all">
                        Read Article <ArrowRight size={15} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
